import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { Loading, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { MachineType } from '@/consts/machine'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import { formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { SelectMachineWarning } from './select-machine-warning'

interface SnapshotSelectorProps {
  machine?: Machine
  value?: Snapshot
  type: MachineType
  domains: number[]
  onChange: (value?: Snapshot) => void
  onSnapshotsChange?: (value: Snapshot[]) => void
}

export function SnapshotSelector({
  value,
  machine,
  type,
  domains,
  onChange,
  onSnapshotsChange,
}: SnapshotSelectorProps) {
  const [loading, setLoading] = useState(false)
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [page, setPage] = useState(1)
  const pageSize = 99

  const getSnapList = useCallback(
    async (p = page) => {
      if (!machine) return
      setLoading(true)

      const params = {
        machine_category: type,
        page: p,
        per_page: pageSize,
        agent_id: machine.agent_id,
        owner_type: 1,
        status: [3],
        domains: domains,
      }

      const res = await API.getSnapList(params)

      if (res.code === 0) {
        setPage(res.pagination.page)
        setSnapshots(res.snapshots.map((snap) => new Snapshot(snap)))
        onSnapshotsChange?.(res.snapshots.map((snap) => new Snapshot(snap)))
      }

      setLoading(false)
    },
    [page, machine, type],
  )

  useEffect(() => {
    if (machine) {
      getSnapList(1)
      onChange(undefined)
    }
  }, [machine])

  if (!machine) {
    return <SelectMachineWarning />
  }

  return (
    <div className="bg-white rounded-2xl py-6">
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && snapshots.length > 0 && (
        <div className="grid grid-cols-4 gap-4 max-h-[300px] overflow-y-auto">
          {snapshots.map((snap) => (
            <div
              key={snap.resID}
              className={cn('p-4 border border-[#E1E4EA] rounded-lg cursor-pointer', {
                'bg-[#F1FCF8] border-[#2bcaa5]': value?.resID === snap.resID,
                'opacity-50 cursor-not-allowed': snap.disabled,
              })}
              onClick={() => !snap.disabled && onChange(snap)}
            >
              <div className="t-xs mb-3 text-primary">{snap.resID}</div>
              <div className="t-sb mb-0.5 text-primary">{snap.name}</div>
              <p className="text-secondary t-xs">
                Created at {formatDatePreset(snap.createdAt, 'minute')}
              </p>
              {snap.disabled && <div className="text-error mt-2">{snap.errMsg}</div>}
            </div>
          ))}
        </div>
      )}
      {!loading && snapshots.length === 0 && (
        <EmptyView
          title={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
            </span>
          }
        />
      )}
      {/*<Pagination*/}
      {/*  size={pageSize}*/}
      {/*  total={total}*/}
      {/*  current={page}*/}
      {/*  className="mt-4 justify-end"*/}
      {/*  onChange={(page) => getSnapList(page)}*/}
      {/*/>*/}
    </div>
  )
}
